"use strict";
/**
 * Polymarket US adapter — a thin shim over the existing PM-US modules.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.pmUsAdapter = void 0;
exports.extractPmUsFilledQty = extractPmUsFilledQty;
const polymarket_us_1 = require("../api/polymarket-us");
const polymarket_us_fetcher_1 = require("../markets/polymarket-us-fetcher");
const cache_1 = require("../markets/cache");
const polymarket_us_orderbook_1 = require("../polymarket-us-orderbook");
const orderbook_1 = require("../orderbook");
const polymarket_us_creation_1 = require("../orders/polymarket-us-creation");
const state_1 = require("../state");
/**
 * Shares filled, summed across execution legs.
 *
 * `EXECUTION_TYPE_NEW` legs carry `lastShares: "0.000"`, so summing everything
 * is safe. Zero means the IOC missed, not that the request failed.
 */
function extractPmUsFilledQty(response) {
    return (response.data?.executions ?? []).reduce((total, exec) => total + parseFloat(exec.lastShares ?? "0"), 0);
}
/**
 * Top-of-book price for the side being traded, from the cached book.
 * A null side (one-sided book) has no price; callers treat 0 as absent.
 */
function pmUsFillPrice(side, action) {
    const cached = (0, state_1.getState)().cachedBidAsk;
    if (!cached)
        return 0;
    if (action === "buy") {
        return (side === "yes" ? cached.yesAsk : cached.noAsk) ?? 0;
    }
    return (side === "yes" ? cached.yesBid : cached.noBid) ?? 0;
}
function fallbackOrderId() {
    const cryptoObj = globalThis.crypto;
    if (cryptoObj?.randomUUID)
        return `pm-us-${cryptoObj.randomUUID()}`;
    const raw = Math.random().toString(36).slice(2);
    return `pm-us-${Date.now()}-${raw.padEnd(12, "0").slice(0, 12)}`;
}
exports.pmUsAdapter = {
    id: "pm-us",
    label: "Polymarket US",
    orderErrorLabel: "PM-US order",
    capabilities: {
        leagueSelector: "flat-list",
        // The gateway labels both outcomes explicitly.
        sideLabels: "both",
        sellFullPosition: false,
        fanOutTarget: true,
        sportModes: true,
        fractionalQuantity: true,
        priceTicks: "uniform",
        orderbookIntervalMs: 250,
        pnlDetail: true,
        marketOrder: "id-length",
    },
    fetchEvents(query) {
        return (0, polymarket_us_fetcher_1.fetchPolymarketUsEvents)(query.tagSlug ?? query.category);
    },
    fetchEventMarkets(eventId) {
        return (0, polymarket_us_fetcher_1.fetchPolymarketUsEventMarkets)(eventId);
    },
    async fetchLeagues() {
        const response = await (0, polymarket_us_1.listPolymarketUsSports)();
        const payload = response.data ?? [];
        // The gateway has sent both an array and a `{ sports: [...] }` wrapper.
        const sports = Array.isArray(payload)
            ? payload
            : Array.isArray(payload.sports)
                ? payload.sports
                : [];
        return sports.map((sport) => {
            const name = String(sport.sport ?? sport.name ?? sport.slug ?? "Unknown");
            return {
                value: String(sport.slug ?? sport.sport ?? sport.name ?? ""),
                label: name,
                sortKey: name.toLowerCase(),
                id: Number(sport.id ?? 0),
                slug: String(sport.slug ?? ""),
            };
        });
    },
    cachedEvents() {
        return (0, cache_1.getPolymarketUsEventCache)().map(polymarket_us_fetcher_1.polymarketUsEventToDisplayEvent);
    },
    startOrderbook(market, opts) {
        return (0, polymarket_us_orderbook_1.startPolymarketUsOrderbookPolling)(market.id, (response) => {
            // The server pre-buckets PM-US books; parse the display directly.
            opts.onUpdate((0, orderbook_1.parseDisplayOrderbook)(response.display, response.ageMs ?? null));
        }, opts.intervalMs ?? this.capabilities.orderbookIntervalMs);
    },
    buildOrderRequest(ctx) {
        return (0, polymarket_us_creation_1.createPolymarketUsOrderRequest)(ctx.marketId, ctx.side, ctx.action, ctx.options);
    },
    async placeOrder(request, ctx) {
        const orderRequest = request;
        const response = await (0, polymarket_us_1.placePolymarketUsOrder)(orderRequest);
        if (!response.success) {
            throw new Error("PM-US order failed");
        }
        if (response.data?.errors?.length) {
            throw new Error(`PM-US order errors: ${response.data.errors
                .map((error) => error.message)
                .join("; ")}`);
        }
        // PM-US reports no fill price or quantity at placement, so both are
        // estimated from the book we priced against. Read once, after the await,
        // exactly as the previous implementation did.
        const fillPrice = pmUsFillPrice(ctx.side, ctx.action);
        let quantity = 0;
        if (orderRequest.action === "sell") {
            quantity = orderRequest.sellQuantity ?? 0;
        }
        else if (orderRequest.dollarAmount) {
            if (fillPrice > 0) {
                quantity = orderRequest.dollarAmount / fillPrice;
            }
            else {
                console.warn(`[PM-US ORDER] Missing fill price for ${ctx.marketId} (${ctx.side} ${ctx.action}); unable to estimate quantity`);
            }
        }
        const filledQty = extractPmUsFilledQty(response);
        const orderId = response.data?.id ?? response.data?.orders?.[0]?.order?.id ?? null;
        return {
            // success=true with zero filled shares is a miss, not a success.
            succeeded: filledQty > 0,
            filledQty,
            statusForState: null,
            order: {
                order_id: orderId || fallbackOrderId(),
                side: ctx.side,
                quantity,
                fill_price: fillPrice,
                status: "pending",
                created_at: new Date().toISOString(),
            },
            raw: response,
            ...(response.warning ? { warning: response.warning } : {}),
        };
    },
};
